import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { KnownIssue, KnownIssueCreate, KnownIssueUpdate } from '../api/useKnownIssues.ts';
import type { Product } from '../api/useProducts.ts';
import type { SubmissionDetail } from '../api/useSubmissions.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { saksnr } from '../innboks/saksnummer.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Bryter } from './Bryter.tsx';
import { LESER_GRUNN_KF } from './Detalj.tsx';
import { Om } from '../skall/Om.tsx';

/** Kontraktens tak: KnownIssueCreate.title og .workaround. */
export const TITTEL_MAKS = 80;
export const OMVEI_MAKS = 140;

export type SkjemaModus = { navn: 'ny' } | { navn: 'rediger'; feil: KnownIssue } | { navn: 'fraSak'; sak: SubmissionDetail };

export interface SkjemaProps {
  modus: SkjemaModus;
  produkter: Product[];
  /** Ukjent er strengest (ux-flow-architect): til produktene er lastet, kan ingenting lagres; feilet, sies det — aldri «ingen produkter». */
  produkterTilstand: 'laster' | 'feil' | 'ok';
  kanSkrive: boolean;
  opptatt: boolean;
  onOpprett: (body: KnownIssueCreate) => void;
  onOppdater: (id: string, body: KnownIssueUpdate) => void;
  onAvbryt: () => void;
  /** Under breakpointen (DD-51) tar skjemaet listas plass; «Tilbake til lista» står øverst. */
  onTilbake?: () => void;
}

const liste = (tekst: string): string[] => tekst.split(',').map((s) => s.trim()).filter(Boolean);

/**
 * Feltet: etiketten i body halvfet med bi-leddet («· valgfri, tomt er alle») og telleren i
 * høyre kant. Hjelpetekst BARE der feltet ikke forklarer seg selv — ÉN i hele skjemaet
 * (F3: femten hjelpetekster ble én). En forklart lås og en tilstandstekst er ikke
 * hjelpetekst og teller ikke mot taket.
 */
function Felt({ label, bi, teller, tellerId, hjelp, bred, children }: { label: string; bi?: string; teller?: string; tellerId?: string; hjelp?: string; bred?: boolean; children: ReactNode }) {
  return (
    <label className={`panel-kf-felt${bred ? ' panel-kf-felt-bred' : ''}`}>
      <span className="panel-kf-etikett">
        {label}
        {bi ? <span className="panel-kf-biledd">· {bi}</span> : null}
        {teller ? (
          <span className="panel-kf-teller" data-testid={tellerId}>
            {teller}
          </span>
        ) : null}
      </span>
      {children}
      {hjelp ? (
        <span className="panel-kf-hjelp" data-testid="kf-hjelp">
          {hjelp}
        </span>
      ) : null}
    </label>
  );
}

/**
 * Oppføringsskjemaet (R3.3 — kjente feil-konsept v0.1 skjerm 4): SJU felt og ÉN
 * hjelpesetning — tittelens, fordi det er den innsenderen ser. Samme skjema er «Ny kjent
 * feil», «Rediger» og «Gjør til kjent feil» fra en sak. Tre deler (DD-30): tittel i
 * brukerens ord, omvei, versjonen den rettes i; skjermer og versjoner er kontraktens
 * `screens[]`/`versions[]` — en kjent feil har ingen plattform.
 *
 * Reglene står ÉN gang, bak «Om skjermer, versjoner og hvem som ser kortet», og ikke ved
 * feltene (F3, F15). Skjulingen står der, og bare for rettede: dagens hjelpetekst sa den på
 * aktive også, og backend (`KnownIssueVisibility`) skjuler bare ved status `fikset`
 * (DD-30 presisert 2026-09-19, avvik 1).
 */
export function Skjema({ modus, produkter, produkterTilstand, kanSkrive, opptatt, onOpprett, onOppdater, onAvbryt, onTilbake }: SkjemaProps) {
  const feil = modus.navn === 'rediger' ? modus.feil : null;
  const sak = modus.navn === 'fraSak' ? modus.sak : null;
  const [tittel, setTittel] = useState(feil?.title ?? sak?.text.slice(0, TITTEL_MAKS) ?? '');
  const [omvei, setOmvei] = useState(feil?.workaround ?? '');
  const [rettesI, setRettesI] = useState(feil?.fixedIn ?? '');
  const [skjermer, setSkjermer] = useState(feil ? feil.screens.join(', ') : (sak?.context.screen ?? ''));
  const [versjoner, setVersjoner] = useState(feil ? feil.versions.join(', ') : '');
  const [widget, setWidget] = useState(feil?.visibleInWidget ?? true);
  const [beta, setBeta] = useState(feil?.visibleToTesters ?? true);
  const [produktId, setProduktId] = useState(feil?.productId ?? produkter[0]?.id ?? '');
  const tittelRef = useRef<HTMLTextAreaElement | null>(null);
  const rettet = feil?.status === 'fikset';

  useEffect(() => {
    tittelRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!produktId && produkter[0]) setProduktId(produkter[0].id);
  }, [produkter, produktId]);

  const tom = tittel.trim() === '';
  // Validering etter interaksjon (ux-flow-architect): rød ramme først etter at feltet er forlatt eller lagring er forsøkt — aldri på et tomt, urørt skjema.
  const [tittelRort, setTittelRort] = useState(false);
  const grunn = !kanSkrive
    ? LESER_GRUNN_KF
    : tom
      ? 'Tittel kan ikke være tom — det er den widgeten viser'
      : !feil && produkterTilstand === 'laster'
        ? 'Henter produkter …'
        : !feil && produkterTilstand === 'feil'
          ? 'Kunne ikke hente produktene — prøv igjen'
          : !feil && !produktId
            ? 'Ingen produkter — kortet må høre til et produkt'
            : opptatt
              ? 'Lagrer …'
              : null;

  const lagre = () => {
    setTittelRort(true);
    if (grunn) return;
    const felles = {
      title: tittel.trim(),
      workaround: omvei.trim() || undefined,
      screens: liste(skjermer),
      versions: liste(versjoner),
      fixedIn: rettesI.trim() || undefined,
      visibleInWidget: widget,
      visibleToTesters: beta,
    };
    if (feil) {
      onOppdater(feil.id, { ...felles, workaround: omvei.trim(), fixedIn: feil.status === 'fikset' ? rettesI.trim() || feil.fixedIn : rettesI.trim() });
    } else {
      onOpprett({ ...felles, productId: produktId, ...(sak ? { fromSubmissionId: sak.id } : {}) });
    }
  };

  const overskrift = feil ? feil.title : sak ? 'Gjør til kjent feil' : 'Ny kjent feil';
  const steg = feil ? 'Rediger' : sak ? 'Gjør til kjent feil' : 'Ny kjent feil';

  return (
    <section
      aria-labelledby="kf-skjema-h"
      className="panel-detalj panel-kf-skjema"
      data-testid="kf-skjema"
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
          e.preventDefault();
          lagre();
        } else if (e.key === 'Escape') {
          e.stopPropagation();
          onAvbryt();
        }
      }}
    >
      {onTilbake ? (
        <button type="button" className="panel-knapp-sekundaer panel-tilbake" onClick={onTilbake}>
          <Ikon navn="arrow-back-outline" /> Tilbake til lista
        </button>
      ) : null}
      <p className="panel-detalj-meta">
        <button type="button" className="panel-knapp-lenke panel-kf-crumb" onClick={onAvbryt}>
          <Ikon navn="arrow-back-outline" /> Kjente feil
        </button>{' '}
        · {steg}
      </p>
      <h2 id="kf-skjema-h" className="panel-detalj-tittel">
        {overskrift}
      </h2>

      {/* Fra sak: ÉN linje som sier hva lagringen gjør med saken og innsenderen (avvik 22). */}
      {sak ? (
        <p role="status" className="panel-kf-fra-sak">
          <Ikon navn="link-outline" /> Fra sak <b>{saksnr(sak.number)}</b> — saken settes til Kjent feil og innsenderen får «Vi vet» når du lagrer.
        </p>
      ) : null}

      <div className="panel-kf-felter">
        {/* Hjelpesetningen — den ENE i skjemaet. */}
        <Felt bred label="Hva de ser" bi="i innsenderens ord" teller={`${tittel.length}/${TITTEL_MAKS}`} tellerId="kf-teller-tittel" hjelp="Dette er teksten innsenderen ser under «Er det dette du ser?».">
          <textarea ref={tittelRef} value={tittel} onChange={(e) => setTittel(e.target.value)} onBlur={() => setTittelRort(true)} maxLength={TITTEL_MAKS} rows={2} aria-required="true" aria-invalid={(tittelRort && tom) || undefined} disabled={!kanSkrive} data-testid="kf-tittel" />
        </Felt>

        <Felt bred label="Omvei" bi="valgfri" teller={`${omvei.length}/${OMVEI_MAKS}`} tellerId="kf-teller-omvei">
          <textarea value={omvei} onChange={(e) => setOmvei(e.target.value)} maxLength={OMVEI_MAKS} rows={2} placeholder="Hva de kan gjøre i mellomtiden." disabled={!kanSkrive} data-testid="kf-omvei" />
        </Felt>

        <Felt label={rettet ? 'Rettet i versjon' : 'Rettes i versjon'} bi={rettet ? undefined : 'valgfri'}>
          <input type="text" value={rettesI} onChange={(e) => setRettesI(e.target.value)} placeholder="3.4.0" maxLength={32} disabled={!kanSkrive} data-testid="kf-rettes-i" />
          {/* Forklart lås, ikke hjelpetekst: en rettet feil kan redigeres, men versjonen varselet
              ble sendt med kan ikke tømmes (KnownIssueUpdate beholder den gamle). */}
          {rettet ? (
            <span className="panel-kf-tilstand">
              <Ikon navn="lock-closed-outline" /> Kan ikke tømmes — varselet er sendt med denne.
            </span>
          ) : null}
        </Felt>

        {!feil && produkter.length > 1 ? (
          <Felt label="Produkt">
            <select value={produktId} onChange={(e) => setProduktId(e.target.value)} disabled={!kanSkrive} data-testid="kf-produkt">
              {produkter.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Felt>
        ) : null}

        <Felt label="Skjermer" bi="valgfri, tomt er alle">
          <input type="text" value={skjermer} onChange={(e) => setSkjermer(e.target.value)} placeholder="Nedlastinger, Avspiller" disabled={!kanSkrive} data-testid="kf-skjermer" />
        </Felt>

        <Felt label="Gjelder versjoner" bi="valgfri, tomt er alle">
          <input type="text" value={versjoner} onChange={(e) => setVersjoner(e.target.value)} placeholder=">=3.2.0 <3.4.0" className="panel-kf-mono" disabled={!kanSkrive} data-testid="kf-versjoner" />
        </Felt>

        <div className="panel-kf-felt panel-kf-felt-bred panel-kf-synlighet">
          <span className="panel-kf-etikett">Vises</span>
          <span className="panel-kf-brytere">
            <Bryter label="I widgeten" paa={widget} onChange={setWidget} grunn={kanSkrive ? null : LESER_GRUNN_KF} testid="kf-skjema-widget" />
            <Bryter label="I programmet" paa={beta} onChange={setBeta} grunn={kanSkrive ? null : LESER_GRUNN_KF} testid="kf-skjema-beta" />
          </span>
          {/* Tilstandstekst, ikke hjelpetekst: den sier hva valget betyr nå (P3). */}
          {!widget && beta ? <span className="panel-kf-tilstand">Vises bare i programmet</span> : null}
          {!widget && !beta ? (
            <span className="panel-kf-tilstand panel-kf-tilstand-varsel">
              <Ikon navn="warning-outline" /> Ingen ser denne — den finnes bare i registeret.
            </span>
          ) : null}
        </div>
      </div>

      {/* REGLENE, ett sted. */}
      <Om tittel="skjermer, versjoner og hvem som ser kortet" testid="kf-om-regler">
        <p>Widgeten søker bare blant feil for skjermen brukeren står på. Tomt felt betyr alle skjermer.</p>
        <p>
          Versjonene er områder, for eksempel <code>&gt;=3.2.0 &lt;3.4.0</code>. Tomt felt betyr alle versjoner.
        </p>
        <p>Er feilen rettet, skjules kortet i tillegg for alle versjoner som er nyere enn den den er rettet i. En aktiv feil skjules aldri.</p>
        <p>«I widgeten» er feilsporet i appen og på nett. «I programmet» er betaprogrammets aksept og fane.</p>
      </Om>

      <div className="panel-detalj-fot">
        <Knapp label={sak ? 'Lagre og lenk saken' : 'Lagre'} kbd="Ctrl+Enter" primaer grunn={grunn} onClick={lagre} testid="kf-lagre" />
        <Knapp label="Lukk" kbd="Esc" onClick={onAvbryt} testid="kf-avbryt" />
      </div>
    </section>
  );
}
